import { Injectable, Inject } from '@angular/core';
import { Observable, BehaviorSubject, throwError, Subject, Subscription, timer, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { GlobalComponentService, parseJWTtoken, ReusableInfoAlertComponent } from '@techmore/global';
import { BackStagePayload } from '../../../share/entity/back-stage-payload';
import { ITemplate } from '../../../share/entity/i-template';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from '../../../share/tokens/generator-env.token';
import { AbstractTemplateService } from '../../../share/service/abstract-template-service';
import { ServerConfigService } from '../../../share/service/server-config.service';
import { TemplateType } from '../../../share/entity/TemplateType';
import { DateTime } from 'luxon';
import { ITokenService } from '../../../member/declaration/i-token-service';
import { Template } from '../../../share/entity/Template';

@Injectable({
  providedIn: 'root',
})
export class BackStageTemplateService implements ITokenService {
  /** 是否正在刷新token中，預設為否 */
  protected _isRefreshing: BehaviorSubject<boolean|Error> = new BehaviorSubject<boolean|Error>(false);
  /** 模板服務 */
  templateService: AbstractTemplateService<Template>;
  /** 模板類型 */
  type: TemplateType;
  /** 最後一次操作畫面的時間戳記 */
  private lastedActiveTimeStamp: number;
  /** 超時警告計時器訂閱 */
  private timeoutAlertTimerSubc: Subscription;
  /** 超時警告計時器訂閱 */
  private timeoutAlert2TimerSubc: Subscription;
  /** 超時計時器訂閱 */
  private timeoutTimerSubc: Subscription;
  private readonly _messageReplySubject = new Subject<unknown>();

  constructor(
    @Inject(GENERATOR_ENV_TOKEN) private readonly generatorEnv: GeneratorEnv,
    private readonly route: ActivatedRoute,
    private readonly globalComponentService: GlobalComponentService,
    protected readonly serverConfigService: ServerConfigService,
  ) {}

  listenOnWindowMessage(isIndexPage: boolean): Subject<BackStagePayload> {
    if (isIndexPage) {
      this.type = TemplateType.index;
    }
  
    const subj = new Subject<BackStagePayload>();
    window.addEventListener('message', (event) => this._handleWindowMessage(event, subj), false);
    return subj;
  }
  
  private _handleWindowMessage(event: MessageEvent, subj: Subject<BackStagePayload>): void {
    if (!this._isAllowedDomain(event.origin)) {
      return;
    }
  
    const dataFromParent = event.data;
    if (dataFromParent) {
      this._messageReplySubject.next(dataFromParent);
  
      if (this.type === TemplateType.index) {
        this._handleIndexPageMessage(dataFromParent, subj);
      } else {
        console.error('未匹配路由的網址');
      }
    }
  }
  
  private _isAllowedDomain(origin: string): boolean {
    return this.generatorEnv.allowBackStageDomain.includes(origin);
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _handleIndexPageMessage(dataFromParent: any, subj: Subject<BackStagePayload>): void {
    switch (dataFromParent.type) {
      case 're_appInit':
        this._handleAppInit(dataFromParent, subj);
        break;
      case 're_timeoutRefresh':
        this._handleTimeoutRefresh(dataFromParent);
        break;
      case 'close':
        window.close();
        break;
      default:
        console.error('未知的消息類型:', dataFromParent.type);
    }
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _handleAppInit(dataFromParent: any, subj: Subject<BackStagePayload>): void {
    sessionStorage.setItem(this.generatorEnv.accessTokenKey, dataFromParent.payload.accessToken);
    sessionStorage.setItem(this.generatorEnv.refreshTokenKey, dataFromParent.payload.refreshToken);
    this.startReadyToTimeOutTimer(dataFromParent.payload.accessToken, dataFromParent.payload.refreshToken);
    subj.next(dataFromParent.payload);
  
    if (!this.serverConfigService.isEditModeUrl) {
      this.serverConfigService.isEditModeUrl = true;
    }
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _handleTimeoutRefresh(dataFromParent: any): void {
    sessionStorage.setItem(this.generatorEnv.accessTokenKey, dataFromParent.payload.accessToken);
    sessionStorage.setItem(this.generatorEnv.refreshTokenKey, dataFromParent.payload.refreshToken);
    this.startReadyToTimeOutTimer(dataFromParent.payload.accessToken, dataFromParent.payload.refreshToken);
  
    if (this._isRefreshing.value) {
      this._isRefreshing.next(false);
    }
  }

  /** 註冊模板服務實例 */
  registTemplateService(service: AbstractTemplateService<ITemplate>, type: TemplateType) {
    if (type === this.type) {
      this.templateService = service;
    }
  }

  refreshToken$(): Observable<void> {
    return new Observable<void>(observer => {
      if (this._isRefreshing.getValue() !== true) {
        this._isRefreshing.next(true);

        this.refreshMemberToken(sessionStorage.getItem(this.generatorEnv.refreshTokenKey)).subscribe(() => {
          this._isRefreshing.next(false);
        }, error1 => {
          this._isRefreshing.next(error1);
        });
      }

      this._isRefreshing.pipe(
        filter(refreshResult => refreshResult !== true),
        take(1),
      ).subscribe(refreshResult => {
        if (refreshResult === false) {
          observer.next();
          observer.complete();
        } else {
          observer.error(refreshResult as Error);
          observer.complete();
        }
      });
    });
  }

  /** 實作向後台刷新token方法 */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected refreshMemberToken = (refreshToken: string): Observable<object> => {
    this.requestUpdateToken();

    return this._messageReplySubject.pipe(
      filter((dataFromParent) => dataFromParent['type'] === 'timeoutRefreshFail' || dataFromParent['type'] === 're_timeoutRefresh'),
      take(1),
      switchMap(dataFromParent => {
        if (dataFromParent['type'] === 'timeoutRefreshFail') {
          return throwError(new Error('timeoutRefreshFail'));
        } else if (dataFromParent['type'] === 're_timeoutRefresh') {
          return of(dataFromParent['payload']);
        }
        return throwError(new Error('BackStageTemplateService refreshMemberToken wrong type'));
      }),
    );
  }

  /** 登出流程，將清空資料後關閉視窗
   * @param fromUser 是否為後台主動觸發的登出，預設為真
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  logout(fromUser = true, cb?: () => void): Observable<unknown> {
    if (!fromUser) {
      const stageUrl = decodeURIComponent(atob(decodeURIComponent(this.route.snapshot.queryParams['stage'])));
      window.opener.postMessage('timeoutLogout', stageUrl);
    }
    sessionStorage.removeItem(this.generatorEnv.accessTokenKey);
    sessionStorage.removeItem(this.generatorEnv.refreshTokenKey);
    window.close();
    return of(true);
  }

  canLogout(): boolean {
    return true;
  }

  getAuthToken(): string {
    return sessionStorage.getItem(this.generatorEnv.accessTokenKey);
  }

  getJWTpayload(): object {
    return parseJWTtoken(this.getAuthToken());
  }

  startReadyToTimeOutTimer(accessToken: string, refreshToken: string) {
    this.lastedActiveTimeStamp = new Date().getTime();
  
    this._clearExistingTimers();
  
    const { expTime, refreshExpTime } = this._parseTokenTimes(accessToken, refreshToken);
    const alertTime = this._calculateAlertTime(expTime, refreshExpTime);
  
    this._setupTimeoutAlertTimer(alertTime, expTime);
    this._setupSecondAlertTimer(refreshExpTime, expTime);
    this._setupFinalTimeoutTimer(refreshExpTime, expTime);
  }
  
  /** 清除現有的計時器 */
  private _clearExistingTimers(): void {
    if (this.timeoutAlertTimerSubc) {
      this.timeoutAlertTimerSubc.unsubscribe();
      delete this.timeoutAlertTimerSubc;
    }
    if (this.timeoutAlert2TimerSubc) {
      this.timeoutAlert2TimerSubc.unsubscribe();
      delete this.timeoutAlert2TimerSubc;
    }
    if (this.timeoutTimerSubc) {
      this.timeoutTimerSubc.unsubscribe();
      delete this.timeoutTimerSubc;
    }
  }
  
  /** 解析 accessToken 和 refreshToken 的到期時間 */
  private _parseTokenTimes(accessToken: string, refreshToken: string): { expTime: number; refreshExpTime: number } {
    const jwtPayload = parseJWTtoken(accessToken);
    const expTime = jwtPayload['exp'] * 1000;
  
    const jwtPayload2 = parseJWTtoken(refreshToken);
    const refreshExpTime = jwtPayload2['exp'] * 1000;
  
    return { expTime, refreshExpTime };
  }
  
  /** 計算警告時間 */
  private _calculateAlertTime(expTime: number, refreshExpTime: number): number {
    let alertTime = expTime + 5 * 60 * 1000; // 到期後 5 分鐘內的警告時間
    if (refreshExpTime - expTime > 2 * 60 * 1000) {
      if (alertTime > (refreshExpTime - 60000)) {
        alertTime = (expTime + refreshExpTime) / 2;
      }
    } else {
      alertTime = expTime;
    }
    return alertTime;
  }
  
  /** 設置第一個超時警告計時器 */
  private _setupTimeoutAlertTimer(alertTime: number, expTime: number): void {
    const alertTimeDiff = alertTime - new Date().getTime();
    if (alertTimeDiff > 0) {
      this.timeoutAlertTimerSubc = timer(alertTimeDiff).subscribe(() => {
        if (DateTime.fromMillis(this.lastedActiveTimeStamp) <= DateTime.fromMillis(expTime - 10 * 60 * 1000)) {
          this.globalComponentService.openSanckBar('還在編輯嗎? 別忘了存檔喔!');
        } else {
          this.requestUpdateToken();
        }
      });
    }
  }
  
  /** 設置第二個超時警告計時器 */
  private _setupSecondAlertTimer(refreshExpTime: number, expTime: number): void {
    const alertTime2 = refreshExpTime - 60000; // refreshToken 到期前 1 分鐘
    const timeOutAlert2Diff = alertTime2 - new Date().getTime();
    if (timeOutAlert2Diff > 0) {
      this.timeoutAlert2TimerSubc = timer(timeOutAlert2Diff).subscribe(() => {
        if (DateTime.fromMillis(this.lastedActiveTimeStamp) <= DateTime.fromMillis(expTime - 10 * 60 * 1000)) {
          this.globalComponentService.openInfoAlert('連線將於 1 分鐘內到期，請盡速存檔!');
        } else {
          this.requestUpdateToken();
        }
      });
    }
  }
  
  /** 設置最終超時計時器 */
  private _setupFinalTimeoutTimer(refreshExpTime: number, expTime: number): void {
    const untilExpTimeDiff = refreshExpTime - new Date().getTime();
    if (untilExpTimeDiff > 0) {
      this.timeoutTimerSubc = timer(untilExpTimeDiff).subscribe(() => {
        if (DateTime.fromMillis(this.lastedActiveTimeStamp) <= DateTime.fromMillis(expTime - 10 * 60 * 1000)) {
          (this.globalComponentService.globalCmp['infoAlert'] as ReusableInfoAlertComponent).infoConfirm.close();
          this.globalComponentService.openInfoAlert('連線已斷開，您已經太久沒操作畫面。<br>請重新開啟編輯頁面');
        } else {
          this.requestUpdateToken();
        }
      });
    }
  }

  /** 要求目前畫面維持連線 */
  keepConnection() {
    this.lastedActiveTimeStamp = new Date().getTime();
  }

  /** 向後台視窗要求更新存取權杖 */
  requestUpdateToken() {
    const stageUrl = decodeURIComponent(atob(decodeURIComponent(this.route.snapshot.queryParams['stage'])));
    window.opener.postMessage('timeoutRefresh', stageUrl);
  }
}
